import merge from 'lodash/object/merge';

// Used internally, exporting for testing purposes only
function getLastFromStringOrArray(stringOrArray) {
  return [].concat(stringOrArray).pop();
}

// Used internally, exporting for testing purposes only
//
// This method mimicks the Angular 1.x parsing for $location.search()... for the most part.
// It differs in that the Angular parser sets the value to `true` for key/value pairs
// without an `=`. This method sets the value to `null` as the spec
// states: https://www.w3.org/TR/2012/WD-url-20120524/#collect-url-parameters
function parseUrlSearchString() {
  var locationSearchString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

  // Transforms a string `?test1=test1&test2=test2&test1=anothertest1` into an object
  //
  //  {
  //    test1: ['test1', 'anothertest1'],
  //    test2: 'test2'
  //  }
  //
  //
  return locationSearchString.trim().replace(/^\?/, '').split('&').reduce(reducePairsToObject, {});

  function reducePairsToObject(ret, pair) {
    var parts = pair.split('=');
    var key = tryDecodeURIComponent(parts[0]);
    // https://www.w3.org/TR/2012/WD-url-20120524/#collect-url-parameters
    var val = parts[1] ? tryDecodeURIComponent(parts[1]) : null;

    if (key) {
      ret[key] = ret.hasOwnProperty(key) ? [].concat(ret[key], val) : val;
    }

    return ret;
  }
}

function tryDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    // Ignore any invalid URI component.
  }
}

// A map of environments to subdomain environment name
var ENVIRONMENTS = {
  DEMO: 'demo',
  DEVELOPMENT: 'dev',
  DOCKER: 'docker',
  PRODUCTION: 'production',
  RUNNABLE: 'runnableapp',
  STAGING: 'staging',
  TEST: 'test'
};

var DEFAULT_ENV = ENVIRONMENTS.TEST;

function buildEnvironment() {
  var environments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      env = _ref.env,
      extendEnv = _ref.extendEnv;

  var envs = Object.assign({}, environments);

  return merge({}, envs.base, envs[env] || envs[DEFAULT_ENV], envs[extendEnv]);
}

function getBuildOptionsFromLocation(location) {
  location = location || {};
  var search = parseUrlSearchString(location.search);
  var hostnameEnv = getEnvironmentFromHostname(location.hostname);

  // using `merge` over `Object.assign` because merge doesn't
  // add properties that have a value of `undefined`.
  return merge({}, {
    env: getLastFromStringOrArray(search.env) || hostnameEnv,
    extendEnv: getLastFromStringOrArray(search['extend-env'])
  });
}

// Used internally, exporting for testing purposes only
function getEnvironmentFromHostname() {
  var hostname = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

  var parts = (hostname || '').split('.').reverse();
  var domain = parts[1];

  switch (domain) {
    case 'updater':
      // Return either the env (ex. `staging`) or `updater` (i.e. production)
      // Need to do this since env hosts have 4 parts and production only has 3
      return parts.length > 3 ? parts[2] : ENVIRONMENTS.PRODUCTION;

    case ENVIRONMENTS.DOCKER:
    case ENVIRONMENTS.RUNNABLE:
      return domain;

    default:
      return DEFAULT_ENV;
  }
}

function buildEnvironmentFromLocation(environments) {
  var location = (window || {}).location;
  return buildEnvironment(environments, getBuildOptionsFromLocation(location));
}

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

/*
 * Build an object of environment names in the form:
 *
 *  {
 *    PRODUCTION: 'production',
 *    STAGING: 'staging',
 *  }
 *
 *  Takes a single `environments` configuration object in the form:
 *
 *  {
 *    production: {
 *      name: 'production',
 *      endpoints: { ... }
 *      keys: { ... }
 *    }
 *  }
 */
function buildEnvironmentNames(environments) {
  if (!isFilledObject(environments)) {
    throw new Error("[buildEnvironmentNames] No environments found in: " + environments);
  }

  return Object.keys(environments).reduce(reduceToName, {});

  function reduceToName(current, key) {
    var NAME = environments[key].name;
    return NAME ? Object.assign({}, current, defineProperty({}, NAME.toUpperCase(), NAME)) : current;
  }
}

function isFilledObject(obj) {
  return obj && obj instanceof Object && Object.keys(obj).length;
}

export { buildEnvironment, buildEnvironmentFromLocation, buildEnvironmentNames };
